import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Database, Download, Trash2, AlertTriangle, Shield, Wrench, Gift, Sparkles, FileSearch, ArrowLeft, History, Mail, RefreshCw, TrendingUp, Eye, MessageSquareWarning, Check, Archive, MessageCircleReply, Send, X, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import { Section, KpiTile, SCOPE_LABELS } from "./_shared";

// =========================== AUDIT LOG ===========================
function AuditTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [action, setAction] = useState("");
  const [skip, setSkip] = useState(0);
  const [weeklyExports, setWeeklyExports] = useState([]);
  const [exportBusy, setExportBusy] = useState(false);
  const limit = 50;

  const load = async () => {
    const params = new URLSearchParams();
    if (action) params.set("action", action);
    params.set("limit", String(limit));
    params.set("skip", String(skip));
    const { data } = await teacherApi.get(`/admin/audit?${params}`);
    setRows(data.rows || []);
    setTotal(data.total || 0);
  };
  const loadWeekly = async () => {
    const { data } = await teacherApi.get("/admin/audit/weekly-exports");
    setWeeklyExports(data.exports || []);
  };
  useEffect(() => { load(); }, [skip, action]);
  useEffect(() => { loadWeekly(); }, []);

  const downloadCSV = async () => {
    const params = new URLSearchParams();
    if (action) params.set("action", action);
    try {
      const res = await teacherApi.get(`/admin/audit/export?${params}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : "audit-log.csv";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("CSV pobrany");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd eksportu");
    }
  };

  const runWeeklyNow = async () => {
    setExportBusy(true);
    try {
      const { data } = await teacherApi.post("/admin/audit/weekly-exports/run-now");
      toast.success(`Eksport zapisany: ${data.export.key} (${data.export.rows} wpisów)`);
      loadWeekly();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd weekly export");
    } finally { setExportBusy(false); }
  };

  return (
    <Section icon={FileSearch} title="Dziennik audytu">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Input
          value={action}
          onChange={(e) => { setSkip(0); setAction(e.target.value); }}
          placeholder="Filtruj po akcji (np. admin.backup, session.create)"
          className="brut-input border-2 border-[#0F172A] rounded-md h-10 max-w-[400px]"
          data-testid="audit-filter-action"
        />
        <Button
          size="sm"
          onClick={downloadCSV}
          className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="audit-export-csv-btn"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />Eksport CSV
        </Button>
        <span className="text-sm font-bold ml-auto">Wpisów: {total}</span>
      </div>
      <div className="space-y-1 max-h-[400px] overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} className="brut border-2 border-[#0F172A] rounded-md p-2 bg-white text-xs font-mono" data-testid={`audit-row-${i}`}>
            <div className="flex gap-3 flex-wrap">
              <span className="text-[#64748B]">{new Date(r.ts).toLocaleString("pl")}</span>
              <span className="font-black">{r.action}</span>
              <span>{r.actor_email || r.actor_id}</span>
              {r.target_type && <span className="text-[#64748B]">→ {r.target_type}:{r.target_id?.slice(0, 8)}</span>}
            </div>
            {r.metadata && Object.keys(r.metadata).length > 0 && (
              <div className="text-[#475569] mt-1 break-all">{JSON.stringify(r.metadata)}</div>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-4">
        <Button
          size="sm"
          disabled={skip === 0}
          onClick={() => setSkip(Math.max(0, skip - limit))}
          className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="audit-prev-btn"
        >
          ← Poprzednia
        </Button>
        <Button
          size="sm"
          disabled={skip + limit >= total}
          onClick={() => setSkip(skip + limit)}
          className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
          data-testid="audit-next-btn"
        >
          Następna →
        </Button>
      </div>

      {/* ---------- Weekly archive ---------- */}
      <div className="border-t-2 border-[#0F172A] mt-6 pt-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-xl font-black">Archiwum tygodniowe (RODO)</h3>
            <p className="text-xs text-[#64748B]">
              Co niedzielę o 04:00 UTC zapisywany jest 7-dniowy snapshot logu jako CSV do Object Storage (retencja 53 tygodnie).
            </p>
          </div>
          <Button
            size="sm"
            onClick={runWeeklyNow}
            disabled={exportBusy}
            className="brut brut-hover bg-[#FEF08A] text-[#0F172A] font-bold rounded-md"
            data-testid="weekly-export-run-now-btn"
          >
            {exportBusy ? "Eksportuję..." : "Wymuś eksport teraz"}
          </Button>
        </div>
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {weeklyExports.length === 0 && <div className="text-sm text-[#64748B]">Brak archiwów jeszcze.</div>}
          {weeklyExports.map((e) => (
            <div key={e.id} className="brut border-2 border-[#0F172A] rounded-md p-2 bg-white text-xs font-mono flex items-center justify-between flex-wrap gap-2" data-testid={`weekly-export-${e.id}`}>
              <div>
                <span className="font-black">{e.key}</span>
                <span className="text-[#64748B] ml-3">
                  {new Date(e.created_at).toLocaleString("pl")} · {e.rows} wpisów · {(e.size_bytes / 1024).toFixed(1)} KB · {e.trigger}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
}



export default AuditTab;
